import React, { useState, useEffect, useRef } from 'react';
import { Button } from "../../button/Button";
import { autoResizeTextarea, formatDateTimeDisplay, handleShiftEnter } from "../../../utils/Utils";
import { EditTaskProps } from "../../../types";
import { useTranslation } from 'react-i18next';
import '../../add-task/AddTask.css';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { faBell, faCalendar } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TaskScheduleModal } from '../../task-schedule-modal/TaskScheduleModal';
import { formatTaskScheduleLabel } from '../../../utils/taskScheduleLabel';

export const EditTask: React.FC<EditTaskProps> = ({ saveTodo, task }) => {
    const { t } = useTranslation();
    const [value, setValue] = useState(task.title || task.task || '');
    const [dueDate, setDueDate] = useState<Dayjs | null>(task.dueDate ? dayjs(task.dueDate) : null);
    const [schedule, setSchedule] = useState(task.schedule);
    const [reminderTime, setReminderTime] = useState<Dayjs | null>(task.reminderTime ? dayjs(task.reminderTime) : null);
    const [recurrence, setRecurrence] = useState(task.recurrence);
    const [showScheduleDialog, setShowScheduleDialog] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    
    useEffect(() => {
        if (textareaRef.current) {
            autoResizeTextarea({ target: textareaRef.current });
        }
    }, [value]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (handleShiftEnter(e, value, setValue)) {
            return;
        }
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e);
        }
    };
    
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!value) {
            return;
        }

        // Pass the content, task id, dueDate, and reminderTime
        // The Dashboard will handle updating these fields
        saveTodo(value, task.id, dueDate ? dueDate.toISOString() : undefined, reminderTime ? reminderTime.toISOString() : undefined, recurrence ? { ...recurrence, interval: Math.max(1, recurrence.interval || 1) } : undefined, schedule);
        setValue('');
        setDueDate(null);
        setReminderTime(null);
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <form onSubmit={handleSubmit} className={"todo-add-step-container"}>
                <div className="input-button-row">
                    <textarea
                        ref={textareaRef}
                        placeholder={t("editTask.addStep")}
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        rows={1}
                        className="formatted-input"
                    />
                </div>
                {value && (
                    <div className="task-options-row">
                        <div className="option-group-left">
                            <button type="button" className={`option-icon ${dueDate || reminderTime || recurrence ? 'active' : ''}`} onClick={() => setShowScheduleDialog(true)} aria-label="Task schedule">
                                <FontAwesomeIcon icon={faCalendar} />
                            </button>
                            {dueDate && (
                                <button
                                    type="button"
                                    className="task-schedule-inline-label"
                                    onClick={() => setShowScheduleDialog(true)}
                                    aria-label="Edit task schedule"
                                >
                                    {formatTaskScheduleLabel(dueDate.toDate(), schedule)}
                                </button>
                            )}
                            {reminderTime && (
                                <button
                                    type="button"
                                    className="task-reminder-inline-label"
                                    onClick={() => setShowScheduleDialog(true)}
                                    aria-label="Edit task schedule"
                                >
                                    <FontAwesomeIcon icon={faBell} />
                                    {formatDateTimeDisplay(reminderTime.toISOString())}
                                </button>
                            )}
                        </div>
                        <div className="option-group-right">
                            <Button
                                onClick={handleSubmit}
                                type={"submit"}
                                status={"primary"}
                                text={t("task.save")}
                                icon={undefined}
                                ariaLabel="Save task"
                            />
                        </div>
                    </div>
                )}
                {showScheduleDialog && <TaskScheduleModal dueDate={dueDate} schedule={schedule} reminderTime={reminderTime} recurrence={recurrence}
                    onDueDateChange={setDueDate} onScheduleChange={setSchedule} onReminderTimeChange={setReminderTime} onRecurrenceChange={setRecurrence}
                    onClose={() => setShowScheduleDialog(false)} />}
            </form>
        </LocalizationProvider>
    );
}
